import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import keycloakService from '../services/keycloakService';

const AuthContext = createContext();

/**
 * AuthProvider component - Provides authentication state and control functions
 * @param {Object} props - Component properties
 * @param {React.ReactNode} props.children - Child components that will have access to the context
 * @param {Object} props.config - Keycloak configuration object
 * @param {string} props.config.url - Keycloak server URL
 * @param {string} props.config.realm - Realm name
 * @param {string} props.config.clientId - Client ID
 * @returns {JSX.Element} The context provider wrapper component
 */
export const AuthProvider = ({ children, config }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [authError, setAuthError] = useState(null);

  const isInitialized = useRef(false);

  /**
   * Initialize Keycloak on component mount
   */
  useEffect(() => {
    const initAuth = async () => {
      if (isInitialized.current) {
        return;
      }

      if (!config) {
        console.error('Keycloak configuration is required');
        setAuthError(new Error('Keycloak configuration is required'));
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const authenticated = await keycloakService.initKeycloak(config);

        setIsAuthenticated(authenticated);

        if (authenticated) {
          const userInfo = keycloakService.getUserInfo();
          setUser(userInfo);
          console.log('User authenticated:', userInfo);
        } else {
          console.log('User not authenticated');
        }

        isInitialized.current = true;
      } catch (error) {
        console.error('Failed to initialize Keycloak:', error);
        setAuthError(error);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [config]);

  /**
   * Login to Keycloak
   * Redirects to Keycloak login page
   * Preserves the current URL path so user returns to their intended destination
   */
  const login = () => {
    try {
      keycloakService.login({
        redirectUri: window.location.href,
      });
    } catch (error) {
      console.error('Login failed:', error);
      setAuthError(error);
    }
  };

  /**
   * Logout from Keycloak
   * Redirects to Keycloak logout page
   */
  const logout = () => {
    try {
      keycloakService.logout({
        redirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
      setAuthError(error);
    }
  };

  /**
   * Register a new user in Keycloak
   * Redirects to Keycloak registration page
   */
  const register = () => {
    try {
      keycloakService.register({
        redirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Registration failed:', error);
      setAuthError(error);
    }
  };

  /**
   * Get the current access token
   * @returns {string|null} Access token or null if not authenticated
   */
  const getToken = () => {
    return keycloakService.getToken();
  };

  /**
   * Update the token if it's about to expire
   * @param {number} minValidity - Minimum validity time in seconds (default: 5)
   * @returns {Promise<boolean>} True if token was refreshed, false otherwise
   */
  const updateToken = async (minValidity = 5) => {
    try {
      const refreshed = await keycloakService.updateToken(minValidity);
      if (refreshed) {
        console.log('Token refreshed');
      }
      return refreshed;
    } catch (error) {
      console.error('Failed to update token:', error);
      setAuthError(error);
      return false;
    }
  };

  /**
   * Check if the user has a specific role
   * @param {string} role - Role name to check
   * @returns {boolean} True if user has the role, false otherwise
   */
  const hasRole = (role) => {
    return keycloakService.hasRole(role);
  };

  const ADMIN_ROLE = 'admin';

  /**
   * Check if the user has the admin role in Keycloak
   * @returns {boolean} True if user has the admin role, false otherwise
   */
  const hasAdminRole = () => {
    return hasRole(ADMIN_ROLE);
  };

  /**
   * Refresh user information from the current token
   */
  const refreshUserInfo = () => {
    if (isAuthenticated) {
      const userInfo = keycloakService.getUserInfo();
      setUser(userInfo);
    }
  };

  /**
   * Clear authentication error
   */
  const clearError = () => {
    setAuthError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        authError,
        login,
        logout,
        register,
        getToken,
        updateToken,
        hasRole,
        hasAdminRole,
        refreshUserInfo,
        clearError,
        keycloakInstance: keycloakService.getInstance(),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to use the auth context
 * @returns {Object} The auth context object containing:
 * - isAuthenticated: Boolean indicating if user is authenticated
 * - isLoading: Boolean indicating if authentication is initializing
 * - user: Object containing user information (username, email, firstName, lastName, roles)
 * - authError: Error object if authentication failed
 * - login: Function to initiate login
 * - logout: Function to initiate logout
 * - register: Function to initiate user registration
 * - getToken: Function to get current access token
 * - updateToken: Function to refresh token
 * - hasRole: Function to check if user has a specific role
 * - refreshUserInfo: Function to refresh user information
 * - clearError: Function to clear authentication error
 * - keycloakInstance: Raw Keycloak instance for advanced usage
 */
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export default AuthContext;
